import { ProjectState, RootState, Project } from '@/types';
import { GetterTree, MutationTree, ActionTree, Module } from 'vuex';
import ProjectService from '@/services/project.service';
import { ProjectMutator } from '@/mutators/project.mutator';
import EventBus from '../../services/eventbus.service';

type ProjectGetter = GetterTree<ProjectState, RootState>;

export const initialState = (): ProjectState => {
  return {
    initialState,
    projects: [],
    total: 0,
    activeProjectId: 0,
    filters: {},
  };
};

export const getters: ProjectGetter = {
  projects: (state) => state.projects,
  projectFilters: (state) => state.filters,
  projectsByIds: (state) => (ids: number[]) => state.projects.filter((i: Project) => ids.includes(i.id)),
  projectById: (state) => (id: number) => state.projects.find((i: Project) => i.id === id),
  projectsTabular: (state) => state.projects.map(ProjectMutator.toTabular),
  getProjectById: (state) => (id: number) => state.projects.find((item) => item.id === id),
  activeProject: (state) => state.projects.filter((item: Project) => item.id === state.activeProjectId),
  activeProjectId: (state) => state.activeProjectId,
};

export const mutations: MutationTree<ProjectState> = {
  addProjects(state, newProjects) {
    state.total = newProjects.total;
    state.projects = newProjects.projects;
    state.filters = newProjects.columnFilters;
  },
  addProject(state, newProject) {
    state.total = state.total + 1;
    state.projects.push(newProject);
  },
  updateProject(state, updatedProject: Project) {
    state.projects[state.projects.findIndex((project: Project) => project.id === updatedProject.id)] = updatedProject;
  },
  selectProject(state, id) {
    state.activeProjectId = Number(id);
  },
  deleteProject(state, deleteProject) {
    state.total = state.total--;
    state.projects = state.projects.filter((project: Project) => project.id !== deleteProject.id);
  },
  refreshProject(state, projectToRefresh) {
    state.projects[state.projects.findIndex((project: Project) => project.id === projectToRefresh.id)] = projectToRefresh;
  },
};

export const actions: ActionTree<ProjectState, RootState> = {
  async addProjectsAsync(context, pagination: {
    take: number, skip: number, sort: string, desc: boolean, filters: Record<string, Record<string, boolean>>,
    searchNames: string,
  }) {
    const response = await ProjectService.getProjects(pagination.take,
      pagination.skip,
      pagination.sort,
      pagination.desc,
      pagination.filters,
      pagination.searchNames);

    context.commit('addProjects', response);
    return response;
  },
  async createProjectAsync(context, details) {
    const proj = await ProjectService.createProject(details);
    context.commit('addProject', proj);
    return proj;
  },
  async updateProjectAsync(context: any, project: { id: number, payload: object }) {
    const proj = await ProjectService.updateProject(project.id, project.payload);
    context.commit('updateProject', proj);
    return proj;
  },
  async deleteProjectAsync(context, project) {
    await ProjectService.deleteProject(project);
    context.commit('deleteProject', project);
  },
  async changeProjectAsync(context: any, params: {projectId: number, forceRender: boolean}) {
    context.commit('selectProject', params.projectId);
    EventBus.$emit('global-orgproj-change', {type: 'project', id: params.projectId, forceRender: params.forceRender});
  },
  async refreshProject(context: any, projectId) {
    const proj = await ProjectService.getListDetailsForProjectById(projectId);
    context.commit('refreshProject', proj);
    return proj;
  },
};

export const projects: Module<ProjectState, RootState> = {
  state: initialState(),
  getters,
  mutations,
  actions,
};
